import { useCallback, useMemo } from 'react';
import { FeedSortType } from '@ai-platform/utils';
import type { Collection } from '@ai-platform/database';
import { FeedFormula } from '@ai-platform/utils';

import { useGetFeedQuery, feedApi } from '../../store/services/feed';
import { useSessionHash } from '../../hooks/useSessionHash';
import { useExperimentFlag } from '../../contexts/ExperimentsContext';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { selectFeedPrompts, selectCourses, selectChallenges, selectFeedCache } from '../../store/slices/prompts';
import { useFeedSelector } from '../../store/slices/feed';

// Тип промпта для frontend
export type Prompt = {
    id: string;
    title: string;
    description: string;
    categories: string[];
    prompt: string;
    prompt_for_example: string;
    example: string;
    imageName: string;
    imageData: {
        url?: string;
        colors: {
            MainColor: string;
        };
    };
    video: string;
    likes: number;
    visits: number;
    // Properties expected by PromptCard component
    isEditorial: boolean;
    avatar: string;
    image: string;
    isLiked: boolean;
    tags: string[]; // alias for categories
};

type UsePromptsDataReturn = {
    /** Текущий список промптов */
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    prompts?: any[];
    collections?: Collection[];
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    courses?: any[];
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    challenges?: any[];
    /** Состояние загрузки */
    loading: boolean;
    /** Есть ли еще данные для загрузки */
    hasMore: boolean;
    /** Функция для загрузки следующей порции данных */
    loadMore: () => Promise<void>;
    /** Функция для сброса данных */
    reset?: () => void;
};

type UseFeedDataParams = {
    category?: string;
    formulaName?: FeedFormula;
};

/**
 * Хук для работы с данными ленты с поддержкой бесконечной подгрузки
 * @returns Объект с данными и методами для управления лентой
 */
export const useFeedData = (params: UseFeedDataParams = {}): UsePromptsDataReturn => {
    const dispatch = useAppDispatch();
    const { hash } = useSessionHash();

    const { category: feedSelectorCategory, sort, pageSize } = useFeedSelector();
    const category = params.category ?? feedSelectorCategory;

    const formula = params.formulaName ?? (sort === FeedSortType.All ? FeedFormula.MVP : sort);
    const formulaParams = useExperimentFlag('formulaParams');

    // Генерируем ключ кэша
    const cacheKey = [hash, formula, category].filter(Boolean).join('_');

    // Получаем данные из Redux
    const prompts = useAppSelector(state => selectFeedPrompts(state, cacheKey));
    const allCourses = useAppSelector(selectCourses);
    const allChallenges = useAppSelector(selectChallenges);
    const cache = useAppSelector(state => selectFeedCache(state, cacheKey));

    const categories = useMemo(() => (category ? [category] : undefined), [category]);

    // RTK Query только для выполнения запросов
    const { isFetching } = useGetFeedQuery({
        limit: pageSize,
        hash,
        categories,
        formula,
        formulaParams,
    });

    // Функция для загрузки следующей страницы
    const loadMore = useCallback(async (): Promise<void> => {
        if (!cache?.nextCursor || !cache?.hasMorePrompts || isFetching) {
            return;
        }

        try {
            await dispatch(
                feedApi.endpoints.getFeed.initiate(
                    {
                        hash,
                        categories,
                        cursor: cache.nextCursor,
                        limit: pageSize,
                        formula,
                        formulaParams,
                    },
                    { subscribe: false, forceRefetch: true },
                ),
            ).unwrap();
        } catch (error) {
            console.error('Failed to load more feed items:', error);
        }
    }, [
        cache?.nextCursor,
        cache?.hasMorePrompts,
        isFetching,
        dispatch,
        hash,
        formula,
        categories,
        pageSize,
        formulaParams,
    ]);

    // Возвращаем курсы, челленджи и подборки только если есть промпты
    const hasPrompts = prompts.length > 0;
    const shouldShowOnlyPrompts = (
        [
            FeedSortType.Newest,
            FeedSortType.BestOfAll,
            FeedSortType.BestOfMonth,
            FeedSortType.BestOfWeek,
        ] as FeedSortType[]
    ).includes(sort as FeedSortType);

    return {
        prompts,
        collections: hasPrompts && !shouldShowOnlyPrompts ? cache?.collections || [] : [],
        courses: hasPrompts && !shouldShowOnlyPrompts ? allCourses : [],
        challenges: hasPrompts && !shouldShowOnlyPrompts ? allChallenges : [],
        loading: isFetching,
        hasMore: cache?.hasMorePrompts ?? true,
        loadMore,
    };
};
